import { Grid, Paper, Stack } from '@mui/material';
import React from 'react';
import { InputFieldFactory, SearchInput } from '../../../../../components';
import { TranslationKeys } from '../../../../../translations';
import { useIntl } from 'react-intl';
import { DropdownStyled } from '../../../../Connections/Components/EventsToolbar/EventsToolbar.styles';
import { ComparisonItemType } from '../../../../../types/BaselineMeasurements';

type ComparisonToolbarProps = {
  isLoading?: boolean;
  onFilterItemType: (filter: string) => void;
  archiveEvents: () => void;
  archiveAllEvents: () => void;
  processEvents: () => void;
  processAllEvents: () => void;
  declineEvents: () => void;
  declineAllEvents: () => void;
  onSearch: (inputValue: string) => void;
  searchQuery?: string;
};

export const ComparisonToolBar = ({
  isLoading,
  onFilterItemType,
  archiveEvents,
  archiveAllEvents,
  processEvents,
  processAllEvents,
  declineEvents,
  declineAllEvents,
  onSearch,
  searchQuery,
}: ComparisonToolbarProps) => {
  const intl = useIntl();

  const onChangeItemTypeFilter = React.useCallback(
    ({ value }) => {
      onFilterItemType(value);
    },
    [onFilterItemType],
  );

  const onChangeSearchInput = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onSearch(event.target.value);
    },
    [onSearch],
  );

  const dropdownOptions = {
    archive: [
      {
        title: intl.formatMessage({ id: TranslationKeys['baseline_comparison_actions_events_archive-selected'] }),
        tooltip: 'Archive selected items',
        confirmDialog: {
          title: intl.formatMessage({
            id: TranslationKeys['baseline_comparison_actions_events_archive-selected_confirm_title'],
          }),
          message: intl.formatMessage({
            id: TranslationKeys['baseline_comparison_actions_events_archive-selected_confirm_message'],
          }),
        },
        action: archiveEvents,
        disabled: isLoading || false,
      },
      {
        title: intl.formatMessage({ id: TranslationKeys['baseline_comparison_actions_events_archive-all'] }),
        tooltip: 'Archive all items from comparison',
        confirmDialog: {
          title: intl.formatMessage({
            id: TranslationKeys['baseline_comparison_actions_events_archive-all_confirm_title'],
          }),
          message: intl.formatMessage({
            id: TranslationKeys['baseline_comparison_actions_events_archive-all_confirm_message'],
          }),
        },
        action: archiveAllEvents,
        disabled: isLoading || false,
      },
    ],
    process: [
      {
        title: intl.formatMessage({ id: TranslationKeys['baseline_comparison_actions_events_process-selected'] }),
        tooltip: 'Process selected items from comparison',
        confirmDialog: {
          title: intl.formatMessage({
            id: TranslationKeys['baseline_comparison_actions_events_process-selected_confirm_title'],
          }),
          message: intl.formatMessage({
            id: TranslationKeys['baseline_comparison_actions_events_process-selected_confirm_message'],
          }),
        },
        action: processEvents,
        disabled: isLoading || false,
      },
      {
        title: intl.formatMessage({ id: TranslationKeys['baseline_comparison_actions_events_process-all'] }),
        tooltip: 'Process all items from comparison',
        confirmDialog: {
          title: intl.formatMessage({
            id: TranslationKeys['baseline_comparison_actions_events_process-all_confirm_title'],
          }),
          message: intl.formatMessage({
            id: TranslationKeys['baseline_comparison_actions_events_process-all_confirm_message'],
          }),
        },
        action: processAllEvents,
        disabled: isLoading || false,
      },
    ],
    decline: [
      {
        title: intl.formatMessage({ id: TranslationKeys['baseline_comparison_actions_events_decline-selected'] }),
        tooltip: 'Removeeeeee selected items from comparison',
        confirmDialog: {
          title: intl.formatMessage({
            id: TranslationKeys['baseline_comparison_actions_events_decline-selected_confirm_title'],
          }),
          message: intl.formatMessage({
            id: TranslationKeys['baseline_comparison_actions_events_decline-selected_confirm_message'],
          }),
        },
        action: declineEvents,
        disabled: isLoading || false,
      },
      {
        title: intl.formatMessage({ id: TranslationKeys['baseline_comparison_actions_events_decline-all'] }),
        tooltip: 'Removedafaf all items from comparison',
        confirmDialog: {
          title: intl.formatMessage({
            id: TranslationKeys['baseline_comparison_actions_events_decline-all_confirm_title'],
          }),
          message: intl.formatMessage({
            id: TranslationKeys['baseline_comparison_actions_events_decline-all_confirm_message'],
          }),
        },
        action: declineAllEvents,
        disabled: isLoading || false,
      },
    ],
  };

  const selectItemTypeWithAllOption = React.useMemo(() => {
    const allOption = {
      value: 'all',
      element: intl.formatMessage({ id: TranslationKeys.all }),
      default: true,
    };
    const optionsValues: string[] = Object.values(ComparisonItemType);
    const options = optionsValues.map(value => ({
      value: value,
      element: intl.formatMessage({ id: `baseline.comparison.itemType.${value.toLowerCase()}` }),
    }));

    options.unshift(allOption);
    return options;
  }, [intl]);

  return (
    <Paper sx={{ p: 3, mb: 1, borderRadius: 2 }}>
      <Grid container spacing={1} alignItems="center">
        <Grid item xs={12} md={3}>
          <InputFieldFactory
            data-testid={'comparison-item-type-filter'}
            key={`event-scheme-select`}
            disabled={isLoading}
            onChange={onChangeItemTypeFilter}
            field={{
              type: 'select',
              name: 'ItemTypeFilter',
              label: 'Item Type',
              fullWidth: true,
              value: null,
              items: selectItemTypeWithAllOption,
            }}
          />
        </Grid>

        <Grid item xs={12} md={2}>
          <SearchInput
            disabled={isLoading}
            onChange={onChangeSearchInput}
            initialSearchQuery={searchQuery}
            debounceTime={500}
          />
        </Grid>

        <Grid item xs={12} md={7} justifyContent={'right'} alignItems={'right'} display={'flex'}>
          <Stack direction="row" spacing={1} sx={{ position: 'relative', zIndex: 1000 }}>
            <DropdownStyled data-testid={'comparison-archive-item'} options={dropdownOptions.archive} intl />
            <DropdownStyled data-testid={'comparison-process-item'} options={dropdownOptions.process} intl />
            <DropdownStyled data-testid={'comparison-decline-item'} options={dropdownOptions.decline} intl />
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  );
};
